import { useState, useEffect, useCallback } from 'react'
import {
  Sparkles, RefreshCw, ExternalLink, TrendingUp, Clock,
  Shield, Cloud, Brain, Bookmark, ChevronRight,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

const ENTITY_COLORS = [
  'border-l-blue', 'border-l-purple', 'border-l-cyan', 'border-l-amber',
  'border-l-emerald', 'border-l-rose', 'border-l-blue-light', 'border-l-purple-light',
]

export default function ForYouPage() {
  const [watchlist, setWatchlist] = useState<any[]>([])
  const [articles, setArticles] = useState<any[]>([])
  const [threatLevel, setThreatLevel] = useState<any>(null)
  const [cloudIssues, setCloudIssues] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [watchRes, articlesRes, threatRes, cloudRes] = await Promise.allSettled([
        api.get('/api/watchlist'),
        api.get('/api/news/feed', { params: { limit: 200 } }),
        api.get('/api/cyber/threat-level'),
        api.get('/api/cloud/status'),
      ])
      if (watchRes.status === 'fulfilled') setWatchlist(watchRes.value.data.entities || [])
      if (articlesRes.status === 'fulfilled') setArticles(articlesRes.value.data.articles || [])
      if (threatRes.status === 'fulfilled') setThreatLevel(threatRes.value.data)
      if (cloudRes.status === 'fulfilled') {
        setCloudIssues((cloudRes.value.data.providers || []).filter((p: any) => p.overall_status !== 'operational'))
      }
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const watchlistNames = watchlist.map(w => w.name.toLowerCase())
  const personalised = articles.filter(a => {
    const text = ((a.title || '') + ' ' + (a.summary || '')).toLowerCase()
    return watchlistNames.some(name => text.includes(name))
  })

  const recentCutoff = Date.now() - 12 * 60 * 60 * 1000
  const todaysNews = articles.filter(a => {
    try { return new Date(a.timestamp).getTime() > recentCutoff } catch { return false }
  })

  // Group by entity
  const entityArticles: Record<string, any[]> = {}
  for (const a of personalised) {
    const text = ((a.title || '') + ' ' + (a.summary || '')).toLowerCase()
    for (const w of watchlist) {
      if (text.includes(w.name.toLowerCase())) {
        if (!entityArticles[w.name]) entityArticles[w.name] = []
        if (!entityArticles[w.name].some((x: any) => x.title === a.title)) entityArticles[w.name].push(a)
      }
    }
  }

  const sortedEntities = Object.entries(entityArticles).sort((a, b) => b[1].length - a[1].length)

  const bookmark = async (article: any) => {
    try {
      await api.post('/api/content/bookmarks', { article_title: article.title, article_url: article.url || '', article_source: article.source || '' })
    } catch {}
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Sparkles size={20} className="text-amber" /> For You
          </h1>
          <p className="text-[10px] text-text-secondary">
            {personalised.length} articles matching {watchlist.length} watchlist entities &middot; {todaysNews.length} new today
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Alert banners */}
      {(threatLevel?.level === 'critical' || cloudIssues.length > 0) && (
        <div className="flex gap-3 flex-wrap">
          {threatLevel?.level === 'critical' && (
            <div className="card px-4 py-2 border border-rose/30 bg-rose/5 flex items-center gap-2 text-xs text-rose-light">
              <Shield size={14} /> <b>CRITICAL</b> — {threatLevel.critical_cves_7d} critical CVEs
            </div>
          )}
          {cloudIssues.map(p => (
            <div key={p.slug} className="card px-4 py-2 border border-amber/30 bg-amber/5 flex items-center gap-2 text-xs text-amber-light">
              <Cloud size={14} /> {p.name} <b>{p.overall_status.toUpperCase()}</b>
            </div>
          ))}
        </div>
      )}

      {loading && !personalised.length && (
        <div className="flex items-center justify-center h-40">
          <RefreshCw size={24} className="animate-spin text-text-muted" />
          <span className="ml-3 text-text-muted text-sm">Loading your personalised feed...</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Main feed — 3 cols */}
        <div className="lg:col-span-3 space-y-5">
          {sortedEntities.length === 0 && !loading && (
            <div className="card p-8 text-center">
              <Sparkles size={40} className="mx-auto mb-4 text-amber opacity-30" />
              <p className="text-sm text-text-primary mb-1">Your personalised feed is empty</p>
              <p className="text-[11px] text-text-muted">Add entities to your <a href="/app/watchlist" className="text-blue-light hover:underline">watchlist</a> to see relevant articles here.</p>
            </div>
          )}

          {sortedEntities.map(([entity, arts], entityIdx) => (
            <div key={entity} className={clsx('card overflow-hidden border-l-4', ENTITY_COLORS[entityIdx % ENTITY_COLORS.length])}>
              {/* Entity header */}
              <div className="px-4 py-3 bg-surface-hover/30 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-sm font-bold text-text-primary">{entity}</h2>
                  <span className="badge-blue text-[9px]">{arts.length} articles</span>
                  {watchlist.find(w => w.name === entity)?.mentions_7d > 0 && (
                    <span className="text-[9px] text-text-muted flex items-center gap-0.5">
                      <TrendingUp size={9} /> {watchlist.find(w => w.name === entity)?.mentions_7d} mentions (7d)
                    </span>
                  )}
                </div>
              </div>

              {/* Articles */}
              <div className="divide-y divide-border">
                {arts.slice(0, 5).map((a: any, i: number) => (
                  <div key={i} className="px-4 py-3 hover:bg-surface-hover/20 transition-colors group">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-text-primary leading-snug font-medium">
                          {a.url ? (
                            <a href={a.url} target="_blank" rel="noopener" className="hover:underline">{a.title}</a>
                          ) : a.title}
                        </p>
                        {a.summary && (
                          <p className="text-[10px] text-text-secondary mt-1 line-clamp-2 leading-relaxed">{a.summary.slice(0, 200)}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-[9px] font-medium text-text-muted bg-surface-hover px-1.5 py-0.5 rounded">{a.source}</span>
                          <span className="text-[9px] text-text-muted">{relativeTime(a.timestamp)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" onClick={() => bookmark(a)} className="p-1 rounded hover:bg-surface-hover text-text-muted hover:text-amber" title="Bookmark">
                          <Bookmark size={12} />
                        </button>
                        {a.url && (
                          <a href={a.url} target="_blank" rel="noopener" className="p-1 rounded hover:bg-surface-hover text-text-muted hover:text-blue-light" title="Open">
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {arts.length > 5 && (
                <div className="px-4 py-2 bg-surface-hover/20 text-center">
                  <span className="text-[9px] text-text-muted">+{arts.length - 5} more articles</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Today's headlines */}
          <div className="card p-4">
            <h3 className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Clock size={11} /> Today ({todaysNews.length})
            </h3>
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {todaysNews.slice(0, 15).map((a, i) => (
                <div key={i} className="group">
                  <p className="text-[10px] text-text-primary leading-snug">
                    {a.url ? <a href={a.url} target="_blank" rel="noopener" className="hover:underline">{a.title}</a> : a.title}
                  </p>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-[8px] text-text-muted">{a.source}</span>
                    <button type="button" onClick={() => bookmark(a)}
                      className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-amber transition-opacity" title="Bookmark">
                      <Bookmark size={9} />
                    </button>
                  </div>
                </div>
              ))}
              {todaysNews.length === 0 && <p className="text-[10px] text-text-muted">No articles yet today</p>}
            </div>
          </div>

          {/* Watchlist summary */}
          <div className="card p-4">
            <h3 className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-3">Watchlist</h3>
            <div className="space-y-1.5">
              {watchlist.slice(0, 12).map((w, i) => (
                <div key={w.name} className="flex items-center gap-2">
                  <div className={clsx('w-1.5 h-1.5 rounded-full shrink-0', ENTITY_COLORS[i % ENTITY_COLORS.length].replace('border-l-', 'bg-'))} />
                  <span className="text-[10px] text-text-primary flex-1 truncate">{w.name}</span>
                  <span className="text-[10px] font-mono text-text-muted">{w.mentions_7d}</span>
                </div>
              ))}
            </div>
            <a href="/app/watchlist" className="flex items-center gap-1 text-[9px] text-blue-light hover:underline mt-3">
              Manage watchlist <ChevronRight size={9} />
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
